import React, { useRef, useState } from 'react';
import Webcam from 'react-webcam';
import { Camera, RefreshCw, Loader2, Send, Upload } from 'lucide-react';
import axios from 'axios';
import { compressImage, compressBase64Image, getCompressionSettings } from '../utils/imageCompression';
import config from '../config';

// Create axios instance with base URL
const api = axios.create({
  baseURL: config.apiUrl,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000
});

// Create axios instance with base chat URL
const apiChat = axios.create({
  baseURL: config.apiChatUrl,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000
});

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

const CameraComponent: React.FC = () => {
  const webcamRef = useRef<Webcam>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [isCapturing, setIsCapturing] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const [analysis, setAnalysis] = useState<string | null>(null);
  const [showCamera, setShowCamera] = useState(true);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);

  const videoConstraints = {
    facingMode: facingMode,
  };

  const switchCamera = () => {
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    setIsCapturing(true);
    setShowCamera(false);

    try {
      // Show compression status
      setIsCompressing(true);
      
      // Compress the image before processing
      const compressionSettings = getCompressionSettings();
      const compressedImageData = await compressImage(file, compressionSettings);
      
      setIsCompressing(false);
      setCapturedImage(compressedImageData);

      try {
        console.log('apiUrl:', config.apiUrl);
        console.log('apiChatUrl:', config.apiChatUrl);

        // Send to backend for analysis using the api instance
        const response = await api.post('/api/OpenAI/analyze-image', {
          imageBase64: compressedImageData.split(',')[1]
        });

        console.log(response.data);
        console.log(response.data.analysis);

        if (response.data && response.data.analysis) {
          setAnalysis(response.data.analysis);
          setChatMessages([{
            role: 'assistant',
            content: response.data.analysis
          }]);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (error) {
        console.error('Error analyzing image:', error);
        if (axios.isAxiosError(error)) {
          if (error.code === 'ECONNABORTED') {
            alert('Request timed out. Please try again.');
          } else if (error.response) {
            alert(`Server error: ${error.response.status} - ${error.response.data}`);
          } else if (error.request) {
            alert('No response from server. Please check if the server is running.');
          } else {
            alert(`Error: ${error.message}`);
          }
        } else {
          alert('Error analyzing image. Please try again.');
        }
        setShowCamera(true);
      } finally {
        setIsCapturing(false);
      }
    } catch (error) {
      console.error('Error compressing image:', error);
      alert('Error processing image. Please try again.');
      setIsCapturing(false);
      setIsCompressing(false);
      setShowCamera(true);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const capture = async () => {
    if (webcamRef.current) {
      setIsCapturing(true);
      setShowCamera(false);
      const imageSrc = webcamRef.current.getScreenshot();
      
      if (!imageSrc) {
        alert('Failed to capture image');
        setIsCapturing(false);
        setShowCamera(true);
        return;
      }

      try {
        // Show compression status
        setIsCompressing(true);
        
        // Compress the captured image before processing
        const compressionSettings = getCompressionSettings();
        const compressedImageData = await compressBase64Image(imageSrc, compressionSettings);
        
        setIsCompressing(false);
        setCapturedImage(compressedImageData);

        try {
          console.log('apiUrl:', config.apiUrl);
          console.log('apiChatUrl:', config.apiChatUrl);
          // Send to backend for analysis using the api instance
          const response = await api.post('/api/OpenAI/analyze-image', {
            imageBase64: compressedImageData.split(',')[1]
          });

          if (response.data && response.data.analysis) {
            setAnalysis(response.data.analysis);
            setChatMessages([{
              role: 'assistant',
              content: response.data.analysis
            }]);
          } else {
            throw new Error('Invalid response format');
          }
        } catch (error) {
          console.error('Error analyzing image:', error);
          if (axios.isAxiosError(error)) {
            if (error.code === 'ECONNABORTED') {
              alert('Request timed out. Please try again.');
            } else if (error.response) {
              alert(`Server error: ${error.response.status} - ${error.response.data}`);
            } else if (error.request) {
              alert('No response from server. Please check if the server is running.');
            } else {
              alert(`Error: ${error.message}`);
            }
          } else {
            alert('Error analyzing image. Please try again.');
          }
          setShowCamera(true);
        } finally {
          setIsCapturing(false);
          setIsCompressing(false);
        }
      } catch (error) {
        console.error('Error compressing image:', error);
        alert('Error processing image. Please try again.');
        setIsCapturing(false);
        setIsCompressing(false);
        setShowCamera(true);
      }
    }
  };

  const analyzeAnother = () => {
    setAnalysis(null);
    setShowCamera(true);
    setChatMessages([]);
    setCapturedImage(null);
    setIsCompressing(false);
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || isSending || !capturedImage) return;

    const userMessage = newMessage.trim();
    setNewMessage('');
    setIsSending(true);

    // Add user message to chat
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);

    try {
      console.log('apiUrl:', config.apiUrl);
      console.log('apiChatUrl:', config.apiChatUrl);
      const response = await apiChat.post('/api/OpenAI/chat', {
        message: userMessage,
        initialAnalysis: analysis,
        imageBase64: capturedImage.split(',')[1],
        chatHistory: chatMessages
      });

      if (response.data && response.data.response) {
        setChatMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      if (axios.isAxiosError(error)) {
        if (error.code === 'ECONNABORTED') {
          alert('Request timed out. Please try again.');
        } else if (error.response) {
          alert(`Server error: ${error.response.status} - ${error.response.data}`);
        } else if (error.request) {
          alert('No response from server. Please check if the server is running.');
        } else {
          alert(`Error: ${error.message}`);
        }
      } else {
        alert('Error sending message. Please try again.');
      }
    } finally {
      setIsSending(false);
    }
  };

  // Scroll to bottom of chat when new messages arrive
  React.useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  return (
    <div className="camera-container" style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '1rem',
      padding: '1rem',
      height: '100vh',
      backgroundColor: showCamera || isCapturing ? '#000' : '#fff',
    }}>
      {showCamera ? (
        <div style={{
          position: 'relative',
          width: '100%',
          height: '100vh', // Make camera larger - 10% of viewport height
          overflow: 'hidden',
          borderRadius: '1rem',
        }}>
          <Webcam
            audio={false}
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            videoConstraints={videoConstraints}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
          
          {/* Overlay buttons on top of camera */}
          <div style={{
            position: 'absolute',
            bottom: '6rem',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: '2rem',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '1rem',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            borderRadius: '2rem',
            backdropFilter: 'blur(10px)',
          }}>
            <button
              onClick={switchCamera}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: '0.75rem',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
            >
              <RefreshCw size={24} />
            </button>

            <button
              onClick={capture}
              disabled={isCapturing}
              style={{
                background: 'rgba(255, 255, 255, 0.9)',
                border: 'none',
                color: '#000',
                cursor: 'pointer',
                padding: '1rem',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: isCapturing ? 0.5 : 1,
                transition: 'all 0.2s',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              }}
              onMouseOver={(e) => {
                if (!isCapturing) {
                  e.currentTarget.style.transform = 'scale(1.1)';
                }
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <Camera size={32} />
            </button>

            <button
              onClick={triggerFileInput}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: '0.75rem',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
            >
              <Upload size={24} />
            </button>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageUpload}
              accept="image/*"
              style={{ display: 'none' }}
            />
          </div>
        </div>
      ) : isCapturing ? (
        <div style={{
          position: 'relative',
          width: '100%',
          height: '70vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          borderRadius: '1rem',
          transition: 'height 0.3s ease-in-out',
        }}>
          <div style={{ textAlign: 'center', color: 'white' }}>
            <Loader2 size={48} className="animate-spin" style={{ color: 'white', marginBottom: '1rem' }} />
            <div style={{ fontSize: '1.1rem' }}>{isCompressing ? 'Compressing image...' : 'Analyzing image...'}</div>
          </div>
        </div>
      ) : null}

      {!showCamera && chatMessages.length > 0 && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '100%',
          flex: 1,
          overflow: 'hidden',
          gap: '1rem',
        }}>
                                {/* Header with thumbnail and analyze another button */}
           <div style={{
             display: 'flex',
             alignItems: 'center',
             gap: '1rem',
             padding: '1rem',
             paddingTop: '4rem', // Add extra top padding for iOS safe area
             backgroundColor: '#f8f9fa',
             borderRadius: '0.5rem',
             flexShrink: 0,
             border: '1px solid #e9ecef',
           }}>
             {/* Thumbnail image */}
             {capturedImage && (
               <div style={{
                 width: '80px',
                 height: '80px',
                 overflow: 'hidden',
                 borderRadius: '0.5rem',
                 border: '2px solid rgba(255, 255, 255, 0.2)',
                 flexShrink: 0,
               }}>
                 <img
                   src={capturedImage}
                   alt="Captured vehicle"
                   style={{
                     width: '100%',
                     height: '100%',
                     objectFit: 'cover',
                   }}
                 />
               </div>
             )}
             
             {/* Analyze Another Vehicle button */}
             <button
               onClick={analyzeAnother}
               style={{
                 background: 'none',
                 border: '2px solid #2563eb',
                 color: '#2563eb',
                 padding: '0.75rem 1.5rem',
                 borderRadius: '0.5rem',
                 cursor: 'pointer',
                 fontSize: '1rem',
                 transition: 'all 0.2s',
                 flex: 1, // Take up remaining space
               }}
               onMouseOver={(e) => {
                 e.currentTarget.style.backgroundColor = '#2563eb';
                 e.currentTarget.style.color = 'white';
               }}
               onMouseOut={(e) => {
                 e.currentTarget.style.backgroundColor = 'transparent';
                 e.currentTarget.style.color = '#2563eb';
               }}
             >
               Analyze Another Vehicle
             </button>

             {/* CarQ Logo */}
             <div style={{
               flexShrink: 0,
             }}>
               <img
                 src="/carq-logo.png"
                 alt="CarQ Logo"
                 style={{
                   height: '60px',
                   width: 'auto',
                   opacity: 0.9,
                 }}
               />
             </div>
           </div>
          
                     {/* Scrollable chat messages container */}
           <div style={{
             flex: 1,
             overflowY: 'auto',
             padding: '1rem',
             backgroundColor: '#fff',
             borderRadius: '0.5rem',
             display: 'flex',
             flexDirection: 'column',
             gap: '1rem',
             border: '1px solid #e9ecef',
           }}>
            {chatMessages.map((message, index) => (
              <div
                key={index}
                style={{
                  alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '85%',
                  padding: '0.75rem 1rem',
                  borderRadius: '1rem',
                  backgroundColor: message.role === 'user' ? '#2563eb' : '#374151',
                  color: 'white',
                  lineHeight: '1.4',
                }}
              >
                {message.content}
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>

          {/* Fixed form at the bottom */}
          <form onSubmit={sendMessage} style={{
            display: 'flex',
            gap: '0.5rem',
            padding: '1rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '1rem',
            flexShrink: 0,
            border: '1.5px solid #d3d3d3',
          }}>
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Ask about the vehicle..."
              style={{
                flex: 1,
                padding: '0.75rem',
                borderRadius: '0.5rem',
                border: '1px solid #dee2e6',
                backgroundColor: '#fff',
                color: '#212529',
                fontSize: '1rem',
                outline: 'none',
              }}
            />
            <button
              type="submit"
              disabled={isSending || !newMessage.trim()}
              style={{
                background: isSending || !newMessage.trim() ? '#e9ecef' : '#2563eb',
                border: 'none',
                color: isSending || !newMessage.trim() ? '#6c757d' : 'white',
                cursor: isSending || !newMessage.trim() ? 'not-allowed' : 'pointer',
                padding: '0.75rem',
                borderRadius: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
              }}
              onMouseOver={(e) => {
                if (!isSending && newMessage.trim()) {
                  e.currentTarget.style.backgroundColor = '#1d4ed8';
                }
              }}
              onMouseOut={(e) => {
                if (!isSending && newMessage.trim()) {
                  e.currentTarget.style.backgroundColor = '#2563eb';
                }
              }}
            >
              {isSending ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default CameraComponent; 